import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Factory, Response } from 'app/app.factory';
import { CompanyPricing, CompanySLA } from 'app/shared/types/company.types';
import { map, Observable } from 'rxjs';

export interface Company {
    id?: number;
    uuid?: string;
    name?: string;
    email?: string;
    status?: string;
    type?: string;
    subtype?: string;
    pricing?: CompanyPricing;
    metadata?: any;
    setup?: any;
    logs?: any[];
    createdAt?: string;
    updatedAt?: string;
}

@Injectable({
    providedIn: 'root',
})
export class CompanyService {
    private _http = inject(HttpClient);
    private _factory = inject(Factory);

    /**
     * Obtener configuración de precios de la company
     */
    getCompanyPricing(): Observable<{ pricing: CompanyPricing }> {
        return this._http.get<Response<{ pricing: CompanyPricing }>>(`${this._factory.url}/company/pricing`).pipe(this._factory.wrap<{ pricing: CompanyPricing }>());
    }

    /**
     * Actualizar configuración de precios de la company
     */
    updateCompanyPricing(pricing: CompanyPricing): Observable<{ company: Company }> {
        return this._http.patch<Response<{ company: Company }>>(`${this._factory.url}/company/pricing`, { pricing }).pipe(this._factory.wrap<{ company: Company }>());
    }

    /**
     * Crear soft update temporal
     */
    createSoftUpdate(softUpdate: any): Observable<{ company: Company }> {
        return this._http.post<Response<{ company: Company }>>(`${this._factory.url}/company/pricing/soft-update`, { soft_update: softUpdate }).pipe(this._factory.wrap<{ company: Company }>());
    }

    /**
     * Actualizar soft update existente
     */
    updateSoftUpdate(updateId: string, updates: any): Observable<{ company: Company }> {
        return this._http.patch<Response<{ company: Company }>>(`${this._factory.url}/company/pricing/soft-update/${updateId}`, updates).pipe(this._factory.wrap<{ company: Company }>());
    }

    /**
     * Obtener información general de la company
     */
    getCompany(): Observable<{ company: Company }> {
        return this._http.get<Response<{ company: Company }>>(`${this._factory.url}/company`).pipe(this._factory.wrap<{ company: Company }>());
    }

    /**
     * Actualizar información general de la company
     */
    updateCompany(data: Partial<Company>): Observable<{ company: Company }> {
        return this._http.patch<Response<{ company: Company }>>(`${this._factory.url}/company`, data).pipe(this._factory.wrap<{ company: Company }>());
    }

    /**
     * Previsualizar impacto de hard overwrite
     */
    previewHardOverwrite(overwriteData: any): Observable<{ preview: any }> {
        return this._http.post<Response<{ preview: any }>>(`${this._factory.url}/company/pricing/hard-overwrite/preview`, overwriteData).pipe(this._factory.wrap<{ preview: any }>());
    }

    /**
     * Aplicar hard overwrite a clientes
     */
    applyHardOverwrite(overwriteData: any): Observable<{ company?: any; overwrite_result?: any }> {
        return this._http
            .post<Response<{ company?: any; overwrite_result?: any }>>(`${this._factory.url}/company/pricing/hard-overwrite`, overwriteData)
            .pipe(this._factory.wrap<{ company?: any; overwrite_result?: any }>());
    }

    /**
     * Obtener lista de clientes de la empresa
     */
    getClients(search?: string): Observable<{ clients: Array<{ uuid: string; name: string; email?: string; run?: string; display?: string }> }> {
        let params = new URLSearchParams();
        if (search) params.set('search', search);

        const url = params.toString() ? `${this._factory.url}/combobox/clients?${params.toString()}` : `${this._factory.url}/combobox/clients`;
        return this._http.get<Response<Array<{ id: number; uuid: string; name: string; run: string; email: string; display: string }>>>(url).pipe(
            map((response) => ({
                clients: Array.isArray(response.data)
                    ? response.data.map((client) => ({
                          uuid: client.uuid,
                          name: client.name,
                          email: client.email,
                          run: client.run,
                          display: client.display,
                      }))
                    : [],
            }))
        );
    }

    /**
     * Obtener configuración de SLA de la company
     */
    getCompanySLA(): Observable<{ sla: CompanySLA }> {
        return this._http.get<Response<{ sla: CompanySLA }>>(`${this._factory.url}/company/sla`).pipe(this._factory.wrap<{ sla: CompanySLA }>());
    }

    /**
     * Actualizar configuración de SLA de la company
     */
    updateCompanySLA(sla: CompanySLA): Observable<{ company: Company }> {
        return this._http.patch<Response<{ company: Company }>>(`${this._factory.url}/company/sla`, { sla }).pipe(this._factory.wrap<{ company: Company }>());
    }

    /**
     * Excel #33: disponibilidad de servicios y vehículos a nivel empresa.
     * Devuelve los 8 servicios con flag `enabled` + array `transports` habilitados.
     */
    getServicesAvailability(): Observable<{ services: ServiceAvailabilityItem[] }> {
        return this._http
            .get<Response<{ services: ServiceAvailabilityItem[] }>>(`${this._factory.url}/company/services-availability`)
            .pipe(this._factory.wrap<{ services: ServiceAvailabilityItem[] }>());
    }

    /**
     * Excel #33: reemplaza la disponibilidad de servicios de la empresa.
     * Servicios no listados en el payload heredan defaults (todos habilitados).
     */
    updateServicesAvailability(services: ServiceAvailabilityItem[]): Observable<{ services: ServiceAvailabilityItem[] }> {
        return this._http
            .patch<Response<{ services: ServiceAvailabilityItem[] }>>(`${this._factory.url}/company/services-availability`, { services })
            .pipe(this._factory.wrap<{ services: ServiceAvailabilityItem[] }>());
    }
}

/** Excel #33: shape de un item de disponibilidad de servicio. */
export interface ServiceAvailabilityItem {
    type: string;
    enabled: boolean;
    transports: string[];
}
